import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { adminSuporteApi, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCcw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUS_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'aberto', label: 'Aberto' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'resolvido', label: 'Resolvido' },
  { value: 'fechado', label: 'Fechado' },
];

function statusLabel(status) {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  return found?.label || status || '-';
}

function prioridadeBadgeVariant(prioridade) {
  if (prioridade === 'critica') return 'destructive';
  if (prioridade === 'alta') return 'default';
  if (prioridade === 'media') return 'secondary';
  return 'outline';
}

export default function AdminSuporte() {
  const [loading, setLoading] = useState(true);
  const [resumoEmpresas, setResumoEmpresas] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filtros, setFiltros] = useState({
    empresaId: 'all',
    status: 'todos',
    q: '',
  });
  const [ticketDetalhe, setTicketDetalhe] = useState(null);
  const [salvandoStatus, setSalvandoStatus] = useState(false);
  const [statusRascunho, setStatusRascunho] = useState('aberto');
  const [statusResolucaoRascunho, setStatusResolucaoRascunho] = useState('');
  const [resolucaoArquivos, setResolucaoArquivos] = useState([]);

  const carregar = async () => {
    setLoading(true);
    try {
      const [resumo, lista] = await Promise.all([
        adminSuporteApi.resumoPorEmpresa(),
        adminSuporteApi.listarTickets({
          page: pagination.page,
          limit: pagination.limit,
          empresaId: filtros.empresaId === 'all' ? '' : filtros.empresaId,
          status: filtros.status === 'todos' ? '' : filtros.status,
          q: filtros.q.trim(),
        }),
      ]);
      setResumoEmpresas(Array.isArray(resumo) ? resumo : []);
      setTickets(Array.isArray(lista?.items) ? lista.items : []);
      setPagination((p) => ({ ...p, ...(lista?.pagination || {}) }));
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      toast.error(getDisplayMessage(err, 'Erro ao carregar tickets de suporte.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, [pagination.page, filtros.empresaId, filtros.status]);

  useEffect(() => {
    const t = setTimeout(() => {
      carregar();
    }, 350);
    return () => clearTimeout(t);
  }, [filtros.q]);

  const totais = useMemo(() => {
    return resumoEmpresas.reduce(
      (acc, item) => {
        acc.total += Number(item.total || 0);
        acc.aberto += Number(item.aberto || 0);
        acc.resolvido += Number(item.resolvido || 0);
        return acc;
      },
      { total: 0, aberto: 0, resolvido: 0 }
    );
  }, [resumoEmpresas]);

  const abrirDetalhe = async (ticketId) => {
    try {
      const data = await adminSuporteApi.obterTicket(ticketId);
      setTicketDetalhe(data);
      setStatusRascunho(data?.status || 'aberto');
      setStatusResolucaoRascunho(data?.status_resolucao || '');
      setResolucaoArquivos([]);
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao carregar detalhes do ticket.'));
    }
  };

  const atualizarStatus = async () => {
    if (!ticketDetalhe?.id) return;
    if ((statusRascunho === 'resolvido' || statusRascunho === 'fechado') && !statusResolucaoRascunho.trim()) {
      toast.error('Informe o status da resolução/fechamento.');
      return;
    }
    setSalvandoStatus(true);
    try {
      if (resolucaoArquivos.length > 0) {
        await adminSuporteApi.uploadResolucaoAnexos(ticketDetalhe.id, resolucaoArquivos);
      }
      await adminSuporteApi.atualizarStatus(ticketDetalhe.id, {
        status: statusRascunho,
        status_resolucao:
          statusRascunho === 'resolvido' || statusRascunho === 'fechado'
            ? statusResolucaoRascunho.trim()
            : '',
      });
      const data = await adminSuporteApi.obterTicket(ticketDetalhe.id);
      setTicketDetalhe(data);
      setResolucaoArquivos([]);
      toast.success('Status atualizado.');
      carregar();
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao atualizar status.'));
    } finally {
      setSalvandoStatus(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Suporte</h1>
        <p className="text-slate-600">Gestão de tickets separados por clientes</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total de tickets</p>
            <p className="text-2xl font-bold">{totais.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Em aberto</p>
            <p className="text-2xl font-bold">{totais.aberto}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Resolvidos</p>
            <p className="text-2xl font-bold">{totais.resolvido}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tickets</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <div className="w-full md:w-72">
              <Input
                placeholder="Buscar por cliente, assunto ou categoria"
                value={filtros.q}
                onChange={(e) => setFiltros((f) => ({ ...f, q: e.target.value }))}
              />
            </div>
            <Select
              value={filtros.empresaId}
              onValueChange={(v) => setFiltros((f) => ({ ...f, empresaId: v }))}
            >
              <SelectTrigger className="w-full md:w-72">
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {resumoEmpresas.map((e) => (
                  <SelectItem key={e.empresa_id} value={e.empresa_id}>
                    {e.nome_exibicao} ({e.total})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filtros.status}
              onValueChange={(v) => setFiltros((f) => ({ ...f, status: v }))}
            >
              <SelectTrigger className="w-full md:w-52">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className="ml-auto"
              onClick={() => carregar()}
              disabled={loading}
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-11 w-full" />
              ))}
            </div>
          ) : tickets.length === 0 ? (
            <div className="py-8 text-center text-slate-500">Nenhum ticket encontrado.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="py-2 pr-3">Cliente</th>
                    <th className="py-2 pr-3">Assunto</th>
                    <th className="py-2 pr-3">Categoria</th>
                    <th className="py-2 pr-3">Prioridade</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Data</th>
                    <th className="py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="py-3 pr-3">{t.empresa?.nome_exibicao || '-'}</td>
                      <td className="py-3 pr-3">{t.assunto}</td>
                      <td className="py-3 pr-3">{t.categoria_modulo}</td>
                      <td className="py-3 pr-3">
                        <Badge variant={prioridadeBadgeVariant(t.prioridade)}>
                          {t.prioridade}
                        </Badge>
                      </td>
                      <td className="py-3 pr-3">{statusLabel(t.status)}</td>
                      <td className="py-3 pr-3">{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
                      <td className="py-3 text-right">
                        <Button variant="outline" size="sm" onClick={() => abrirDetalhe(t.id)}>
                          Ver detalhes
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page <= 1}
            onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
          >
            Anterior
          </Button>
          <span className="text-sm text-slate-600">
            Página {pagination.page} de {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
          >
            Próxima
          </Button>
        </div>
      )}

      <Dialog open={!!ticketDetalhe} onOpenChange={() => setTicketDetalhe(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ticket {ticketDetalhe?.id?.slice?.(0, 8)}</DialogTitle>
          </DialogHeader>
          {ticketDetalhe ? (
            <div className="space-y-3 text-sm">
              <p><strong>Cliente:</strong> {ticketDetalhe.empresa?.nome_exibicao}</p>
              <p><strong>Solicitante:</strong> {ticketDetalhe.solicitante?.nome} ({ticketDetalhe.solicitante?.email})</p>
              <p><strong>Assunto:</strong> {ticketDetalhe.assunto}</p>
              <p><strong>Categoria:</strong> {ticketDetalhe.categoria_modulo}</p>
              <p><strong>Prioridade:</strong> {ticketDetalhe.prioridade}</p>
              <p className="whitespace-pre-wrap"><strong>Descrição:</strong> {ticketDetalhe.descricao}</p>
              <div>
                <strong>Anexos:</strong>
                {Array.isArray(ticketDetalhe.anexos) && ticketDetalhe.anexos.length > 0 ? (
                  <ul className="list-disc pl-5 mt-1">
                    {ticketDetalhe.anexos.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          onClick={() => adminSuporteApi.openAnexo(a.upload_blob_id)}
                          className="text-blue-600 hover:underline"
                        >
                          {a.nome_arquivo}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="ml-1 text-slate-500">nenhum</span>
                )}
              </div>
              <div className="pt-2">
                <strong>Status:</strong>
                <div className="mt-2">
                  <Select
                    value={statusRascunho}
                    onValueChange={setStatusRascunho}
                    disabled={salvandoStatus}
                  >
                    <SelectTrigger className="w-56">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.filter((s) => s.value !== 'todos').map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(statusRascunho === 'resolvido' || statusRascunho === 'fechado') && (
                <div className="space-y-2">
                  <strong>Status da resolução/fechamento:</strong>
                  <Textarea
                    value={statusResolucaoRascunho}
                    onChange={(e) => setStatusResolucaoRascunho(e.target.value)}
                    placeholder="Descreva a resolução do chamado ou motivo do fechamento."
                    className="min-h-24"
                  />
                  <div className="space-y-1">
                    <Input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => setResolucaoArquivos(Array.from(e.target.files || []).slice(0, 6))}
                    />
                    {resolucaoArquivos.length > 0 && (
                      <p className="text-xs text-slate-600">{resolucaoArquivos.length} imagem(ns) selecionada(s)</p>
                    )}
                  </div>
                </div>
              )}
              {ticketDetalhe.status_resolucao ? (
                <p className="whitespace-pre-wrap">
                  <strong>Última resolução/fechamento:</strong> {ticketDetalhe.status_resolucao}
                </p>
              ) : null}
              <div>
                <strong>Anexos da resolução:</strong>
                {Array.isArray(ticketDetalhe.resolucao_anexos) && ticketDetalhe.resolucao_anexos.length > 0 ? (
                  <ul className="list-disc pl-5 mt-1">
                    {ticketDetalhe.resolucao_anexos.map((a) => (
                      <li key={a.id}>
                        <button
                          type="button"
                          onClick={() => adminSuporteApi.openAnexo(a.upload_blob_id)}
                          className="text-blue-600 hover:underline"
                        >
                          {a.nome_arquivo}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="ml-1 text-slate-500">nenhum</span>
                )}
              </div>
              <div className="pt-2">
                <Button onClick={atualizarStatus} disabled={salvandoStatus}>
                  {salvandoStatus ? 'Salvando...' : 'Salvar status'}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
